import React, { FC, useEffect } from 'react'
import { LoaderSpinner } from '@lightspeed/design-system-react'
import { ApiTransaction } from '@vend/types'
import { ErrorContent } from '../modal-states/ErrorContent'
import { usePMOFEffectReducer } from '../../hooks/use-pmof-effect-reducer/usePMOFEffectReducer'
import { PMOFArgs } from '../../hooks/use-pmof-effect-reducer/types'

interface Props extends PMOFArgs {
  close: (transaction?: Promise<ApiTransaction>) => void
  setDismissible: (dismissible: boolean) => void
}

export const LSPAYPaymentMethodOnFile: FC<Props> = ({
  amount,
  customerId,
  outletId,
  paymentMethodOnFileId,
  register,
  registerSaleId,
  transactionId,
  close,
  setDismissible,
  setSwiperError,
}) => {
  const [state, dispatch] = usePMOFEffectReducer({
    amount,
    customerId,
    outletId,
    paymentMethodOnFileId,
    register,
    registerSaleId,
    setSwiperError,
    transactionId,
  })

  // Close modal when done
  useEffect(() => {
    if (state.status === 'done') {
      close(Promise.resolve(state.transaction))
    }
  }, [state, close])

  // Control modal dismissibility: the modal cannot be closed while 'processing'
  useEffect(() => {
    setDismissible(state.status !== 'processing')
  }, [state.status, setDismissible])

  switch (state.status) {
    case 'processing': {
      // Keep the spinner visible via the parent modal's loader area in the previous implementation.
      // Here, we just render nothing; modal chrome will still be shown and not dismissible.
      // Alternatively, we could render a minimal spinner container, but the requirement only says that
      // the spinner is visible which the parent already handles by showing modal.
      // Keep minimal to avoid layout shifts.
      return (
        <div
          className="vd-align-center vd-mt10 vd-mb10"
          style={{ transform: 'scale(1.5)' }}
        >
          <LoaderSpinner />
        </div>
      )
    }
    case 'error': {
      return (
        <ErrorContent
          headingType="sub-heading"
          lspayError={state.errorMessage}
          showWarningIcon={true}
          disableRetry={state.disableRetry}
          retry={() => dispatch({ type: 'RETRY' })}
          close={() => close()}
        />
      )
    }
    case 'done':
      return null
    default: {
      const _exhaustiveCheck: never = state
      return _exhaustiveCheck
    }
  }
}
